import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { empresasApi } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';

const REGIMES = [
  { value: 'simples_nacional', label: 'Simples Nacional' },
  { value: 'lucro_presumido', label: 'Lucro Presumido' },
  { value: 'lucro_real', label: 'Lucro Real' }
];

const FORM_VAZIO = { nome: '', razao_social: '', cnpj: '', ie: '', uf: '', municipio: '', regime_tributario: 'simples_nacional' };

export default function EmpresasList() {
  useTituloPagina('Empresas');
  const { tenantId } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [editandoId, setEditandoId] = useState(null); // null = fechado, 'novo' = criando, id = editando
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [enviandoLogoId, setEnviandoLogoId] = useState(null);

  const carregar = () => empresasApi.listar(tenantId).then(setEmpresas).catch(() => {});
  useEffect(() => { carregar(); }, [tenantId]); // eslint-disable-line

  const onChange = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const abrirNova = () => { setForm(FORM_VAZIO); setErro(''); setEditandoId('novo'); };
  const abrirEdicao = (emp) => {
    setForm({
      nome: emp.nome || '', razao_social: emp.razao_social || '', cnpj: emp.cnpj || '',
      ie: emp.ie || '', uf: emp.uf || '', municipio: emp.municipio || '', regime_tributario: emp.regime_tributario
    });
    setErro('');
    setEditandoId(emp.id);
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.nome) { setErro('Nome Fantasia é obrigatório'); return; }
    setSalvando(true);
    setErro('');
    try {
      if (editandoId === 'novo') {
        await empresasApi.criar(form);
      } else {
        await empresasApi.atualizar(editandoId, form);
      }
      setEditandoId(null);
      carregar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const tornarPadrao = async (id) => {
    await empresasApi.tornarPadrao(id);
    carregar();
  };

  const enviarLogo = async (id, arquivo) => {
    if (!arquivo) return;
    setEnviandoLogoId(id);
    try {
      await empresasApi.enviarLogo(id, arquivo);
      carregar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setEnviandoLogoId(null);
    }
  };

  return (
    <div>
      <Card>
        <p style={{ fontSize: 13, color: COLORS.textMid, marginTop: 0 }}>
          Todas as empresas (CNPJs) abaixo compartilham o mesmo catálogo de produtos e estoque do grupo. O regime tributário de cada uma decide como os impostos são calculados na Formação de Preço quando ela estiver selecionada na barra superior.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {empresas.map(emp => (
            <div key={emp.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 8, border: `1px solid ${COLORS.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: COLORS.bg, flexShrink: 0
                }}>
                  {emp.logo_url
                    ? <img src={emp.logo_url} alt={emp.nome} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <span style={{ fontSize: 9, color: COLORS.textLight, textAlign: 'center' }}>sem logo</span>}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{emp.nome} {emp.padrao ? <Badge tom="info">Padrão</Badge> : null}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMid }}>{emp.cnpj || 'sem CNPJ'} · {REGIMES.find(r => r.value === emp.regime_tributario)?.label || emp.regime_tributario}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.primary, cursor: 'pointer', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: '6px 10px' }}>
                  {enviandoLogoId === emp.id ? 'Enviando...' : (emp.logo_url ? 'Trocar Logo' : '+ Logo')}
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    disabled={enviandoLogoId === emp.id}
                    onChange={e => enviarLogo(emp.id, e.target.files[0])} />
                </label>
                <Btn tamanho="sm" variante="ghost" onClick={() => abrirEdicao(emp)}>Editar</Btn>
                {!emp.padrao && <Btn tamanho="sm" variante="ghost" onClick={() => tornarPadrao(emp.id)}>Tornar padrão</Btn>}
              </div>
            </div>
          ))}
          {empresas.length === 0 && <p style={{ color: COLORS.textMid, fontSize: 13 }}>Nenhuma empresa cadastrada.</p>}
        </div>

        {editandoId ? (
          <form onSubmit={salvar} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, borderTop: `1px solid ${COLORS.border}`, paddingTop: 16 }}>
            <strong style={{ gridColumn: '1 / -1', fontSize: 13 }}>{editandoId === 'novo' ? 'Nova empresa' : 'Editando empresa'}</strong>
            <Input label="Nome Fantasia" value={form.nome} onChange={e => onChange('nome', e.target.value)} required />
            <Input label="Razão Social" value={form.razao_social} onChange={e => onChange('razao_social', e.target.value)} />
            <Input label="CNPJ" value={form.cnpj} onChange={e => onChange('cnpj', e.target.value)} />
            <Input label="Inscrição Estadual" value={form.ie} onChange={e => onChange('ie', e.target.value)} />
            <Input label="UF" value={form.uf} onChange={e => onChange('uf', e.target.value)} maxLength={2} />
            <Input label="Município" value={form.municipio} onChange={e => onChange('municipio', e.target.value)} />
            <Select label="Regime Tributário" options={REGIMES} value={form.regime_tributario} onChange={e => onChange('regime_tributario', e.target.value)} />
            {erro && <p style={{ gridColumn: '1 / -1', color: COLORS.danger, fontSize: 13, margin: 0 }}>{erro}</p>}
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
              <Btn variante="ghost" onClick={() => setEditandoId(null)} type="button">Cancelar</Btn>
              <Btn type="submit" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar Empresa'}</Btn>
            </div>
          </form>
        ) : (
          <Btn variante="secondary" onClick={abrirNova}>+ Nova Empresa (CNPJ)</Btn>
        )}
      </Card>
    </div>
  );
}
